import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Bot, Loader2, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import api, { errMsg, fmtDate } from "@/lib/api";
import { AUTOPILOT_MODES } from "@/lib/autopilot";

export default function StatusCard({ cfg, save, reload }) {
  const [running, setRunning] = useState(false);
  const mode = AUTOPILOT_MODES.find((m) => m.v === cfg.mode);
  const ranToday = cfg.last_run_date === cfg.today;

  const run = async () => {
    setRunning(true);
    try { const r = await api.post("/autopilot/run"); toast.success(r.data.message); reload(); }
    catch (e) { toast.error(errMsg(e)); } finally { setRunning(false); }
  };

  return (
    <section className="panel fade-up relative overflow-hidden p-6" data-testid="autopilot-status-card">
      <div className={`absolute inset-y-0 left-0 w-1 ${cfg.enabled ? "bg-emerald-400" : "bg-slate-600"}`} />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${cfg.enabled ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-500/15 text-slate-400"}`}>
            <Bot className={`h-6 w-6 ${cfg.enabled ? "animate-pulse" : ""}`} />
          </div>
          <div>
            <p className="text-lg font-bold" data-testid="autopilot-status-label">Autopilot {cfg.enabled ? "ON" : "OFF"}</p>
            <p className="text-xs text-muted-foreground">
              Mod: <b className="text-slate-200" data-testid="autopilot-status-mode">{mode?.title}</b> · <Link to="/tetapan" className="text-primary hover:underline" data-testid="autopilot-change-mode-link">tukar di Tetapan</Link>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="secondary" onClick={run} disabled={running || !cfg.topics.length} data-testid="autopilot-run-now-btn">
            {running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}Jana sekarang
          </Button>
          <Switch checked={cfg.enabled} onCheckedChange={(v) => save({ enabled: v }, v ? "Autopilot dihidupkan" : "Autopilot dimatikan")} data-testid="autopilot-enabled-toggle" />
        </div>
      </div>
      <div className="mt-5 grid gap-3 text-xs sm:grid-cols-3">
        <Info label="Larian terakhir" value={cfg.last_run_at ? fmtDate(cfg.last_run_at) : "Belum pernah"} id="autopilot-last-run" />
        <Info label="Hari ini" value={ranToday ? "Sudah dijana" : cfg.enabled ? "Akan dijana sebentar lagi" : "-"} id="autopilot-today" />
        <Info label="Keputusan" value={cfg.last_result || "-"} id="autopilot-last-result" />
      </div>
      {cfg.last_error && (
        <p className="mt-4 flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-200" data-testid="autopilot-last-error">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{cfg.last_error}
        </p>
      )}
    </section>
  );
}

function Info({ label, value, id }) {
  return (
    <div className="rounded-lg border border-border bg-background/40 p-3">
      <p className="eyebrow !text-[9px]">{label}</p>
      <p className="mt-1 text-slate-200" data-testid={id}>{value}</p>
    </div>
  );
}
